import { logger } from '../../utils/logger.js'
import { Capacitor } from '@capacitor/core'

const log = logger.create('tle')

const API_BASE = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''

export interface GpRecord {
  OBJECT_NAME: string
  OBJECT_ID: string
  NORAD_CAT_ID: number
  EPOCH: string
  MEAN_MOTION: number
  ECCENTRICITY: number
  INCLINATION: number
  RA_OF_ASC_NODE: number
  ARG_OF_PERICENTER: number
  MEAN_ANOMALY: number
  BSTAR: number
  MEAN_MOTION_DOT: number
  MEAN_MOTION_DDOT: number
  REV_AT_EPOCH: number
  CLASSIFICATION_TYPE?: string
  ELEMENT_SET_NO?: number
  EPHEMERIS_TYPE?: number
}

export type ConstellationName = 'iridium' | 'globalstar' | 'starlink-dtc' | 'ast-spacemobile'

export const CONSTELLATIONS: {
  id: ConstellationName
  name: string
  description: string
  color: string
}[] = [
  {
    id: 'iridium',
    name: 'Iridium',
    description: 'Garmin inReach, Zoleo, YB Tracking',
    color: '#2196F3',
  },
  {
    id: 'globalstar',
    name: 'Globalstar',
    description: 'SPOT, Apple Emergency SOS',
    color: '#FF9800',
  },
  {
    id: 'starlink-dtc',
    name: 'Starlink DTC',
    description: 'T-Mobile direct-to-cell',
    color: '#9C27B0',
  },
  {
    id: 'ast-spacemobile',
    name: 'AST SpaceMobile',
    description: 'AT&T / Verizon direct-to-cell',
    color: '#4CAF50',
  },
]

interface CacheEntry {
  data: GpRecord[]
  fetchedAt: number
}

const CACHE_TTL = 24 * 60 * 60 * 1000

class TleService {
  private cache = new Map<string, CacheEntry>()

  async getConstellation(name: ConstellationName): Promise<GpRecord[]> {
    const cached = this.cache.get(name)
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
      return cached.data
    }

    try {
      const resp = await fetch(`${API_BASE}/api/tle?constellation=${name}`)
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data: GpRecord[] = await resp.json()
      this.cache.set(name, { data, fetchedAt: Date.now() })
      log.info(`Fetched ${data.length} satellites for ${name}`)
      return data
    } catch (err) {
      log.error(`Failed to fetch ${name}:`, err)
      if (cached) return cached.data
      return []
    }
  }

  async search(query: string, limit = 20): Promise<GpRecord[]> {
    try {
      const resp = await fetch(
        `${API_BASE}/api/tle?search=${encodeURIComponent(query)}&limit=${limit}`
      )
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      return resp.json()
    } catch (err) {
      log.error(`Search failed for "${query}":`, err)
      return []
    }
  }

  async getByNoradIds(ids: number[]): Promise<GpRecord[]> {
    try {
      const resp = await fetch(`${API_BASE}/api/tle?noradIds=${ids.join(',')}`)
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      return resp.json()
    } catch (err) {
      log.error(`NORAD ID lookup failed:`, err)
      return []
    }
  }
}

export const tleService = new TleService()
